import { t } from 'i18next'
import TooltipMenu, { Option } from './MoreEventActionsButton'
import {
  DocType,
  EventDoc,
  EventRecurrence,
  EventStatus,
  Exclusion
} from 'types/api/changes'
import { Model } from 'types/model'
import { IEventDetails } from 'hooks/useEventDetails'
import { localEventById } from 'services/db/Queries/Event'
import { is24meApp } from 'utils/appType'
import { Tier } from 'utils/api/accounts'
import { instanceStatus } from 'components/calendar/components/Event'
import { updateEventOnServer } from 'services/api/event'
import { useCalendar } from 'components/providers/CalendarProvider'
import dayjs from 'dayjs'
import { getDefaultTimezone } from 'index'
import { useTfmTourProvider } from 'components/onboarding/TFMTourProvider'
import { useTour } from '@reactour/tour'
import { useEventPopover } from 'components/calendar/eventPopover/EventPopoverProvider'
import { useEventActions } from 'components/providers/EventActionsProvider'
import Logger from 'services/Logger'

interface EventsActionsMenuProps {
  instanceStart: number
  instanceEnd: number
  event?: Model<EventDoc> | EventDoc
  eventHook: IEventDetails
  disabled?: boolean
  onClose: () => void
  closePopup: (b?: boolean) => void
  updateStatus?: (status: EventStatus) => void
}

export default function EventsActionsMenu(props: EventsActionsMenuProps) {
  let options: Option[] = [
    { title: t('duplicate'), neededTier: is24meApp() ? Tier.PRO : undefined }
  ]

  if (
    is24meApp() &&
    (props.event?.Type === DocType.TASK || props.event?.Type === DocType.NOTE)
  ) {
    if (
      instanceStatus(props.event, props.instanceStart) === EventStatus.COMPLETED
    ) {
      options.push({ title: t('uncomplete') })
    } else {
      options.push({ title: t('archive'), color: 'red' })
    }
  }

  let type = t('event').toLowerCase()

  if (props.event?.Type === DocType.TASK) {
    type = t('task').toLowerCase()
  } else if (props.event?.Type === DocType.NOTE) {
    type = t('note').toLowerCase()
  }

  const shareTitle = t('shareDetailsOf', { type: type })
  options.push({
    title: shareTitle
  })

  const calendar = useCalendar()
  const tfmTourProvider = useTfmTourProvider()
  const tour = useTour()
  const { closePopover } = useEventPopover()
  const { duplicateEvent, deleteEvent, shareEvent } = useEventActions()

  return (
    <TooltipMenu
      disabled={props.disabled}
      options={options}
      onSelectOption={function (option: string): boolean {
        Logger.blue('Selected option', { option })
        if (option === t('duplicate')) {
          duplicateEvent(
            props.event as EventDoc,
            props.instanceStart,
            props.instanceEnd
          )
          props.onClose()
          closePopover()
        } else if (option === t('complete')) {
          if (props.updateStatus) props.updateStatus(EventStatus.COMPLETED)
          return true
        } else if (option === t('archive')) {
          if (props.event)
            deleteEvent(
              props.event,
              undefined,
              props.instanceStart,
              props.instanceEnd,
              () => {
                props.onClose()
                closePopover()
              },
              () => {}
            )
          return true
        } else if (option === t('uncomplete')) {
          if (props.updateStatus) {
            localEventById(props.event?._id ?? '')
              .then((event) => {
                if (event.Status === EventStatus.COMPLETED) {
                  const data = {
                    Status: EventStatus.ACTIVE,
                    Recurrence:
                      event.Recurrence &&
                      typeof event.Recurrence !== 'string' &&
                      !('0' in event.Recurrence)
                        ? { ...event.Recurrence, Exclusion: [] }
                        : undefined
                  }

                  updateEventOnServer(event, {
                    ...data,
                    SupplementaryGroupsIDs:
                      props.event?.SupplementaryGroupsIDs ?? []
                  })
                } else if (event.Status === EventStatus.ACTIVE) {
                  const eventRecurrence = event.Recurrence as EventRecurrence
                  updateEventOnServer(event, {
                    Status: EventStatus.ACTIVE,
                    Recurrence: {
                      ...eventRecurrence,
                      Exclusion: [
                        ...(eventRecurrence.Exclusion ?? []),
                        {
                          Date: `${
                            dayjs(props.instanceStart)
                              .add(event.AllDay === '1' ? 12 : 0, 'hour')
                              .tz('UTC')
                              .startOf('day')
                              .valueOf() / 1000
                          }`,
                          Status: EventStatus.ACTIVE,
                          ExclusionDate: dayjs(
                            Number(props.instanceStart) * 1000
                          )
                            .tz(getDefaultTimezone())
                            .format('YYYYMMDDTHHmmss')
                        }
                      ]
                        .reverse()
                        .filter(
                          (item: Exclusion, index: number, self: Exclusion[]) =>
                            index ===
                            self.findIndex(
                              (i: Exclusion) => i.Date === item.Date
                            )
                        )
                        .filter(
                          (it, index, self) =>
                            index ===
                            self.findIndex((i) => {
                              const dayjsExclusionI = dayjs(
                                Number(i.Date) * 1000
                              ).startOf('day')
                              const dayjsExclusionIt = dayjs(
                                Number(it.Date) * 1000
                              ).startOf('day')

                              return (
                                dayjsExclusionI.format('YYYY-MM-DD') ===
                                dayjsExclusionIt.format('YYYY-MM-DD')
                              )
                            })
                        )
                        .filter((exclusion) => {
                          return exclusion.Status !== EventStatus.ACTIVE
                        })
                    },
                    SupplementaryGroupsIDs: event.SupplementaryGroupsIDs ?? []
                  }).then(() => {
                    closePopover()
                  })
                }
              })
              .then(() => {
                closePopover()
              })
          }
          return true
        } else if (option === t('shareDetailsOf', { type: type })) {
          shareEvent(props.event as EventDoc)
          return false
        }

        return true
      }}
    />
  )
}
